import type { Map } from "mapbox-gl"

export function addAgriculturalZones(
	map: Map,
	{
		baseUrl,
		visibleLayers,
		layerName,
		sourceName,
		isSatellite,
	}: {
		baseUrl: string
		sourceName: string
		layerName: string
		visibleLayers: string[]
		isSatellite: boolean
	}
) {
	const visibility = visibleLayers.includes("agricultural-zones") ? "visible" : "none"
	const color = "#367C2B"
	map.addSource(sourceName, {
		type: "vector",
		tiles: [`${baseUrl}/core_public.agricultural_zones_inclusions_exclusions/{z}/{x}/{y}.pbf`],
	})
	map.addLayer({
		id: layerName,
		source: sourceName,
		"source-layer": "core_public.agricultural_zones_inclusions_exclusions",
		type: "fill",
		layout: { visibility },
		paint: {
			"fill-opacity": isSatellite ? 0.35 : 0.2,
			"fill-color": color,
		},
		metadata: { custom: true },
	})
	map.addLayer({
		id: `${layerName}_line`,
		source: sourceName,
		"source-layer": "core_public.agricultural_zones_inclusions_exclusions",
		type: "line",
		layout: { visibility },
		paint: {
			"line-color": color,
			"line-width": isSatellite ? 1 : 0.5,
			"line-opacity": isSatellite ? 0.9 : 0.6,
		},
		metadata: { custom: true },
	})
}

export function addSoilCapabilityForAgricultureFill(
	map: Map,
	{
		baseUrl,
		visibleLayers,
		layerName,
		sourceName,
	}: {
		baseUrl: string
		sourceName: string
		layerName: string
		visibleLayers: string[]
	}
) {
	map.addSource(sourceName, {
		type: "vector",
		tiles: [`${baseUrl}/core_public.soil_agricultural_capability/{z}/{x}/{y}.pbf`],
	})
	map.addLayer({
		id: layerName,
		source: sourceName,
		"source-layer": "core_public.soil_agricultural_capability",
		type: "fill",
		layout: {
			visibility: visibleLayers.includes("soil-capability") ? "visible" : "none",
		},
		paint: {
			"fill-color": "#FFFFFF",
			"fill-opacity": 0,
		},
		minzoom: 11,
		metadata: { custom: true },
	})
}

export function addSoilCapabilityForAgricultureLine(
	map: Map,
	{
		baseUrl,
		visibleLayers,
		layerName,
		sourceName,
	}: {
		baseUrl: string
		sourceName: string
		layerName: string
		visibleLayers: string[]
	}
) {
	map.addSource(sourceName, {
		type: "vector",
		tiles: [`${baseUrl}/core_public.soil_agricultural_capability/{z}/{x}/{y}.pbf`],
	})
	map.addLayer({
		id: layerName,
		source: sourceName,
		"source-layer": "core_public.soil_agricultural_capability",
		type: "line",
		layout: {
			visibility: visibleLayers.includes("soil-capability") ? "visible" : "none",
			"line-join": "round",
			"line-cap": "round",
		},
		paint: {
			"line-color": "#A9784F",
			"line-opacity": 1,
			"line-width": ["interpolate", ["linear"], ["zoom"], 11, 1, 13, 3, 16, 6],
		},
		minzoom: 11,
		metadata: { custom: true },
	})
}

export function addSoilCapabilityForAgricultureLabels(
	map: Map,
	{
		baseUrl,
		sourceName,
		layerName,
		visibleLayers,
	}: {
		baseUrl: string
		sourceName: string
		layerName: string
		visibleLayers: string[]
	}
) {
	map.addSource(sourceName, {
		type: "vector",
		tiles: [`${baseUrl}/core_public.soil_agricultural_capability/{z}/{x}/{y}.pbf`],
	})
	map.addLayer({
		id: layerName,
		source: sourceName,
		"source-layer": "core_public.soil_agricultural_capability",
		type: "symbol",
		layout: {
			"text-field": ["to-string", ["get", "soil_class"]],
			"text-size": ["interpolate", ["linear"], ["zoom"], 6, 8, 10, 10],
			"text-allow-overlap": false,
			"symbol-placement": "point",
			visibility: visibleLayers.includes("soil-capability") ? "visible" : "none",
		},
		paint: {
			"text-color": "#5f482f",
			"text-halo-color": "#FFFFFF",
			"text-halo-width": 2,
		},
		minzoom: 11,
		metadata: { custom: true },
	})
}
